import {
	AccessTimeOutlined,
	AddAlarmOutlined,
	ArrowBackOutlined,
	CalendarMonthOutlined,
	CallOutlined,
	ChevronLeftOutlined,
	ChevronRightOutlined,
	DeleteOutline,
	EditCalendarOutlined,
	ExpandLess,
	ExpandMore,
	RefreshOutlined,
	ViewListOutlined,
	WarningAmberOutlined,
} from '@mui/icons-material';
import {
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Fade,
	IconButton,
	Paper,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme
} from '@mui/material';
import { generateClient, get, post, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useRef, useState } from 'react';
import { getCallbackConfiguration, getContact } from 'src/graphql/queries';
import { ContactPreview } from 'src/pages/screenpop/contactView/contactPreview';
import { useDialTransport, WS_STATUS } from 'src/pages/screenpop/workspace/preview/useDialTransport';
import { getAgentLastCall } from 'src/tools/cacheService';
import { UserContext } from '../../contexts/UserContext';

/**
 * Callbacks component for displaying a campaign's scheduled callbacks.
 * Used within the CallbackWorkspace in the screenpop workspace.
 *
 * When a callback is selected and "Call Now" is clicked, the component
 * transitions to the ContactPreview view with the full contact data.
 * The dialing logic (REST /dial call + call status subscription via useDialTransport)
 * is handled within this component and passed to ContactPreview via props.
 *
 * Props:
 * - agentId: The current agent's ID
 * - campaignId: The specific campaign to show callbacks for
 * - skillId: The campaign's skill/entry point ID
 * - campaignProfileId: The dialing profile ID for the campaign
 * - callbackConfigurationId: The callback configuration ID (contains the script to use)
 */
export function Callbacks({ agentId, campaignId, skillId, campaignProfileId, callbackConfigurationId, onActiveContactChange: onActiveContactChangeProp, refreshKey }) {
	const userContext = useContext(UserContext);
	const client = generateClient();
	const theme = useTheme();

	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [callbacks, setCallbacks] = useState([]);
	const [selectedCallback, setSelectedCallback] = useState(null);
	const [contactData, setContactData] = useState(null);
	const [dialingContact, setDialingContact] = useState(null);
	const [dialActive, setDialActive] = useState(false);
	const [removing, setRemoving] = useState(false);
	const [dialedCallbackIds, setDialedCallbackIds] = useState([]);
	const [weekOffset, setWeekOffset] = useState(0);
	const [viewMode, setViewMode] = useState(() => localStorage.getItem('callbacks-view-mode') || 'calendar');
	const [expandedSections, setExpandedSections] = useState({ overdue: true, ready: true, upcoming: true });
	const [callbackScriptName, setCallbackScriptName] = useState(null);

	// Schedule callback from last call state
	const [lastCallContact, setLastCallContact] = useState(null);
	const [loadingLastCall, setLoadingLastCall] = useState(false);
	const [scheduleOpen, setScheduleOpen] = useState(false);
	const [scheduleTime, setScheduleTime] = useState('');
	const [scheduleNotes, setScheduleNotes] = useState('');
	const [scheduleReleasable, setScheduleReleasable] = useState(false);
	const [scheduling, setScheduling] = useState(false);

	// Reschedule existing callback state
	const [rescheduleOpen, setRescheduleOpen] = useState(false);
	const [rescheduleTime, setRescheduleTime] = useState('');
	const [rescheduleNotes, setRescheduleNotes] = useState('');
	const [rescheduleReleasable, setRescheduleReleasable] = useState(false);
	const [rescheduling, setRescheduling] = useState(false);

	// Call status state (for ContactPreview integration)
	const [callStatus, setCallStatus] = useState('');
	const [connectionLost, setConnectionLost] = useState(false);

	/**
	 * Pending pre-dial check failure awaiting the agent's decision.
	 *
	 * Callbacks do not hard-block on a failed check the way campaign dispatch does.
	 * Instead we hold the failure here, show the reason to the agent, and let them
	 * either place the dial anyway or cancel.
	 *
	 * Shape: `{ reason: string }`, or null when no decision is pending.
	 */
	const [pendingCheckFailure, setPendingCheckFailure] = useState(null);

	/** Resolver for the promise the dial flow awaits while the agent decides. */
	const checkOverrideResolverRef = useRef(null);

	const selectedCallbackRef = useRef(null);
	selectedCallbackRef.current = selectedCallback;

	const dialingContactRef = useRef(null);
	useEffect(() => { dialingContactRef.current = dialingContact; }, [dialingContact]);

	// --- Dial Transport (WS / IoT) ---
	const {
		subscribeToCallStatus,
		checkContact,
	} = useDialTransport({
		agentId,
		tenantId: userContext.tenantId,
		apiKey: userContext.apiKey,
		onCallStatusChange: (message) => {
			const status = message.callStatus || message?.value?.callStatus || '';
			setCallStatus(status);

			const isTerminal = ['Disconnected', 'Call Ended', 'Error', 'Not Allowed To Dial'].some((t) => status?.includes(t));

			if (isTerminal) {
				handleCallComplete();
			}

			// Propagate to workspace
			if (onActiveContactChangeProp && dialingContactRef.current) {
				onActiveContactChangeProp({
					id: dialingContactRef.current.id,
					contactId: dialingContactRef.current.id,
					firstName: dialingContactRef.current.firstName,
					lastName: dialingContactRef.current.lastName,
					phone: dialingContactRef.current.phone,
					cell: dialingContactRef.current.cell,
					externalId: dialingContactRef.current.externalId || '',
					campaignId: dialingContactRef.current.campaignId || '',
					segmentId: dialingContactRef.current.segmentId || '',
					skillId: skillId || '',
					lastDialedNumber: dialingContactRef.current.phone || dialingContactRef.current.cell || '',
					hasCalledOrOnCall: true,
					isCallActive: !isTerminal,
					isLastCallContact: dialingContactRef.current.isLastCallContact || false,
				});
			}
		},
		onAllowDial: null,
		onConnectionStatusChange: (status) => {
			if (status === WS_STATUS.DISCONNECTED) {
				enqueueSnackbar('Connection lost. Attempting to reconnect...', { variant: 'warning', autoHideDuration: 4000 });
				setConnectionLost(true);
			} else if (status === WS_STATUS.CONNECTED) {
				enqueueSnackbar('Connection restored', { variant: 'success', autoHideDuration: 2000 });
				setConnectionLost(false);
			}
		},
	});

	// Subscribe to call status when we enter the dialing view
	useEffect(() => {
		if (agentId && dialingContact) {
			subscribeToCallStatus();
		}
	}, [agentId, dialingContact?.id]);

	function handleViewModeChange(_, newMode) {
		if (newMode) {
			setViewMode(newMode);
			localStorage.setItem('callbacks-view-mode', newMode);
		}
	}

	function toggleSection(section) {
		setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
	}

	// Fetch callbacks on mount and poll every 60s
	useEffect(() => {
		fetchCallbacks();
		const interval = setInterval(fetchCallbacks, 60000);
		return () => clearInterval(interval);
	}, [campaignId]);

	// Refresh when parent triggers a manual refresh
	useEffect(() => {
		if (refreshKey) {
			fetchCallbacks();
			fetchLastCallForScheduling();
		}
	}, [refreshKey]);

	// Fetch the callback configuration's script name
	useEffect(() => {
		async function fetchCallbackScript() {
			if (!callbackConfigurationId) return;
			try {
				const result = await client.graphql({
					query: getCallbackConfiguration,
					variables: { id: callbackConfigurationId },
				});
				const config = result.data.getCallbackConfiguration;
				if (config?.scriptName) {
					setCallbackScriptName(config.scriptName);
				}
			} catch (error) {
				console.error('Error fetching callback configuration', error);
			}
		}
		fetchCallbackScript();
	}, [callbackConfigurationId]);

	// When a callback is selected, fetch the contact details
	useEffect(() => {
		if (selectedCallback) {
			fetchContactData(selectedCallback.contactId);
		} else {
			setContactData(null);
		}
		setRescheduleOpen(false);
	}, [selectedCallback]);

	// Fetch agent's last call to allow scheduling a callback for it (poll every 30s)
	useEffect(() => {
		fetchLastCallForScheduling();
		const interval = setInterval(fetchLastCallForScheduling, 30000);
		return () => clearInterval(interval);
	}, [campaignId, agentId]);

	async function fetchLastCallForScheduling() {
		setLoadingLastCall(true);
		try {
			const agentData = await getAgentLastCall(agentId, userContext.apiKey);

			if (!agentData?.contactId || (agentData.skillId !== skillId && agentData.campaignId !== campaignId)) {
				setLastCallContact(null);
				setLoadingLastCall(false);
				return;
			}

			// Load full contact record
			const contactResponse = await client.graphql({
				query: getContact,
				variables: { id: agentData.contactId },
			});
			const contact = contactResponse.data.getContact;
			if (!contact) {
				setLastCallContact(null);
				setLoadingLastCall(false);
				return;
			}

			if (contact.customFields && typeof contact.customFields === 'string') {
				try { contact.customFields = JSON.parse(contact.customFields); } catch (e) { contact.customFields = null; }
			}

			setLastCallContact({
				id: contact.id,
				contactId: contact.id,
				firstName: contact.firstName,
				lastName: contact.lastName,
				phone: contact.phone,
				cell: contact.cell,
				externalId: contact.externalId || '',
				segmentId: agentData.segmentId || contact.segmentId || '',
				lastDialedNumber: agentData.lastDialedNumber || contact.phone || contact.cell || '',
			});
		} catch (e) {
			console.warn('Could not fetch agent last call for scheduling', e);
			setLastCallContact(null);
		}
		setLoadingLastCall(false);
	}

	function openScheduleForm() {
		const thirtyMinsOut = new Date(Date.now() + 30 * 60 * 1000);
		const year = thirtyMinsOut.getFullYear();
		const month = String(thirtyMinsOut.getMonth() + 1).padStart(2, '0');
		const day = String(thirtyMinsOut.getDate()).padStart(2, '0');
		const hours = String(thirtyMinsOut.getHours()).padStart(2, '0');
		const minutes = String(thirtyMinsOut.getMinutes()).padStart(2, '0');
		setScheduleTime(`${year}-${month}-${day}T${hours}:${minutes}`);
		setScheduleNotes('');
		setScheduleReleasable(false);
		setScheduleOpen(true);
	}

	async function handleScheduleCallback() {
		if (!lastCallContact || !scheduleTime) return;
		setScheduling(true);
		try {
			const session = await fetchAuthSession();
			const response = await post({
				apiName: 'cdyxoutreach',
				path: '/cache/createCallback',
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						campaignId,
						segmentId: lastCallContact.segmentId,
						contactId: lastCallContact.contactId,
						createdById: agentId,
						createdByAgentName: '',
						callBackPOC: lastCallContact.lastDialedNumber,
						callDateTime: new Date(scheduleTime).toISOString(),
						agentId,
						note: scheduleNotes,
						releasable: scheduleReleasable,
					},
				},
			}).response;
			const result = await response.body?.json();
			if (result?.rescheduled) {
				enqueueSnackbar('Callback rescheduled successfully', { variant: 'success' });
				// Un-hide this callback since it was rescheduled (not completed)
				const cbKey = lastCallContact.contactId + ':' + campaignId;
				setDialedCallbackIds(prev => prev.filter(id => id !== cbKey));
			} else {
				enqueueSnackbar('Callback scheduled successfully', { variant: 'success' });
			}
			setScheduleOpen(false);
			fetchCallbacks();
		} catch (error) {
			console.error('Error scheduling callback', error);
			let errMsg = null;
			try {
				if (error?.response?.body) {
					const errBody = await error.response.body.json();
					errMsg = errBody?.msg || errBody?.message || null;
				}
			} catch (e) {
				// Could not parse error body — fall through to default message
			}
			enqueueSnackbar(errMsg || 'Failed to schedule callback', { variant: 'error' });
		}
		setScheduling(false);
	}

	async function fetchCallbacks() {
		try {
			setRefreshing(true);
			const session = await fetchAuthSession();
			const response = await get({
				apiName: 'cdyxoutreach',
				path: `/cache/callBacks/${campaignId}/`,
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
				},
			}).response;

			const result = await response.body?.json();
			let list = [];
			if (result.data) {
				if (Array.isArray(result.data)) {
					list = result.data;
				} else {
					for (const key in result.data) {
						list.push(JSON.parse(result.data[key]));
					}
				}
			}

			// Show only callbacks relevant to this agent (assigned to me or unassigned)
			const visible = list.filter(
				(cb) => cb.agentId === agentId || !cb.agentId,
			);

			// Sort by callDateTime ascending (soonest first)
			visible.sort((a, b) => new Date(a.callDateTime) - new Date(b.callDateTime));

			setCallbacks(visible);
		} catch (error) {
			console.error('Error fetching callbacks', error);
		}
		setLoading(false);
		setRefreshing(false);
	}

	async function fetchContactData(contactId) {
		try {
			const response = await client.graphql({
				query: getContact,
				variables: { id: contactId },
			});
			const contact = response.data.getContact;
			if (contact && contact.customFields) {
				try {
					contact.customFields = JSON.parse(contact.customFields);
				} catch (e) {
					contact.customFields = null;
				}
			}
			setContactData(contact);
		} catch (error) {
			console.error('Error fetching contact', error);
			setContactData(null);
		}
	}

	function getStatusInfo(callback) {
		const now = new Date();
		const callbackTime = new Date(callback.callDateTime);
		const diffMinutes = (callbackTime - now) / 60000;

		if (diffMinutes > 60) {
			return { label: 'Scheduled', color: 'default', variant: 'outlined' };
		} else if (diffMinutes > 0) {
			return { label: 'Upcoming', color: 'info', variant: 'filled' };
		} else if (diffMinutes > -1440) {
			return { label: 'Due Now', color: 'success', variant: 'filled' };
		} else {
			return { label: 'Overdue', color: 'error', variant: 'filled' };
		}
	}

	// ─── Disposition helpers ────────────────────────────────────────────────────

	/**
	 * Sends a disposition to the /disposition endpoint.
	 * Used for both retry-non-increment (delete) and final suppression (suppress in dialer).
	 */
	async function sendDisposition({ callback, contact, dispCode, dispClassName, dispDescription }) {
		const session = await fetchAuthSession();
		const dispositionTime = new Date().toISOString();
		await post({
			apiName: 'cdyxoutreach',
			path: '/disposition',
			options: {
				headers: {
					Authorization: `Bearer ${session.tokens.idToken}`,
					'x-api-key': userContext.apiKey,
				},
				body: {
					Records: [{
						AdditionalInfo: JSON.stringify({
							__typename: 'Contact',
							id: callback.contactId,
							campaignId: callback.campaignId,
							segmentId: callback.segmentId,
							skillId: skillId,
						}),
						IcUserName: agentId,
						IcContactId: '',
						DispositionClassificationId: dispCode,
						DispositionCode: dispCode,
						DispositionClassificationName: dispClassName,
						DispositionDescription: dispDescription,
						PhoneNumber: callback.callBackPOC || contact?.phone || contact?.cell,
						TimeDisposed: dispositionTime,
						CRMId: contact?.externalId || '',
						DialerListId: callback.contactId,
						source: 'Callback',
					}],
				},
			},
		}).response;
	}

	// ─── Actions ────────────────────────────────────────────────────────────────

	/**
	 * Opens the ContactPreview with the selected callback's contact data.
	 */
	function handleDial() {
		if (!selectedCallback || !contactData) return;

		const dialerContact = {
			...contactData,
			phone: selectedCallback.callBackPOC,
			campaignId: selectedCallback.campaignId,
			segmentId: selectedCallback.segmentId,
			isLastCallContact: true,
		};

		setDialActive(false);
		setCallStatus('');
		if (checkOverrideResolverRef.current) resolveCheckOverride(false);
		setDialingContact(dialerContact);
	}

	/**
	 * Called when the agent navigates back from the ContactPreview (via back arrow).
	 * Refreshes the callback list to pick up any changes (e.g. rescheduled callbacks).
	 */
	function handleDialerBack() {
		if (checkOverrideResolverRef.current) resolveCheckOverride(false);
		setDialingContact(null);
		setDialActive(false);
		setCallStatus('');
		setSelectedCallback(null);
		setContactData(null);
		if (onActiveContactChangeProp) onActiveContactChangeProp(null);
		fetchCallbacks();
	}

	/**
	 * Called when the ContactPreview's suppress button is clicked (no dials made).
	 * Sends a final suppression disposition (code 728).
	 */
	async function handleDialerSuppress() {
		const callback = selectedCallbackRef.current;
		if (!callback) return;

		try {
			await sendDisposition({
				callback,
				contact: contactData,
				dispCode: 728,
				dispClassName: 'Callback Removed',
				dispDescription: 'The contact was removed from the callback queue.',
			});
			enqueueSnackbar('Contact suppressed', { variant: 'success' });
		} catch (error) {
			console.error('Error suppressing contact', error);
			enqueueSnackbar('Failed to suppress contact', { variant: 'error' });
		}

		setDialingContact(null);
		setDialActive(false);
		setCallStatus('');
		setSelectedCallback(null);
		setContactData(null);
		if (onActiveContactChangeProp) onActiveContactChangeProp(null);
		fetchCallbacks();
	}

	/**
	 * Called when a call completes from the ContactPreview (terminal call status received).
	 * We do NOT exit the dialer view here — the agent may still want to reschedule.
	 * The agent can click the back button to return to the callback list when done.
	 */
	function handleCallComplete() {
		// Don't clear dialingContact — keep the agent in the ContactPreview view
		// so they can still use the Schedule Callback button after the call ends.
		setDialActive(false);

		// Propagate to workspace so Schedule Callback button state updates
		if (onActiveContactChangeProp && dialingContactRef.current) {
			onActiveContactChangeProp({
				...dialingContactRef.current,
				hasCalledOrOnCall: true,
				isCallActive: false,
			});
		}
	}

	/**
	 * Called when the agent clicks a phone number in ContactPreview.
	 * Places the actual dial call and propagates to workspace-level state.
	 *
	 * @returns {Promise<boolean>} Whether a dial was actually placed. Returning false
	 *   lets ContactPreview keep the number dialable (e.g. the agent cancelled at the
	 *   failed-check prompt).
	 */
	async function handleApprove(phoneNumber) {
		if (!dialingContact) return false;

		setDialActive(true);
		setCallStatus('Initiating Call...');

		// Propagate active contact to workspace
		if (onActiveContactChangeProp) {
			onActiveContactChangeProp({
				id: dialingContact.id,
				contactId: dialingContact.id,
				firstName: dialingContact.firstName,
				lastName: dialingContact.lastName,
				phone: phoneNumber,
				cell: dialingContact.cell,
				externalId: dialingContact.externalId || '',
				campaignId: dialingContact.campaignId || '',
				segmentId: dialingContact.segmentId || '',
				skillId: skillId || '',
				lastDialedNumber: phoneNumber,
				hasCalledOrOnCall: true,
				isCallActive: true,
				isLastCallContact: dialingContact.isLastCallContact || false,
			});
		}

		return await dialContact(phoneNumber);
	}

	/**
	 * Called when the agent dismisses the call status pane in ContactPreview (after call ends).
	 */
	function handleResetApprove() {
		setCallStatus('');
	}

	/**
	 * Runs the pre-dial check for the callback contact, then places the dial.
	 *
	 * Before dialing we run the manual dispatch check using the campaign's dialing
	 * profile. The check validates call windows and curfew, applies the campaign's
	 * suppression (resolved from the contact's segment via useCampaignSuppression),
	 * and assigns an outbound caller ID from the campaign's caller ID configuration
	 * when one is set. We then dial the checked contact so the assigned caller ID
	 * carries through.
	 *
	 * Unlike campaign dispatch, a failed check does not block a callback dial. The
	 * failure reason is surfaced to the agent, who decides whether to dial anyway
	 * or cancel (see `requestCheckOverride`). The check assigns a caller ID even
	 * when it fails, so an override dial still uses the campaign's caller ID.
	 *
	 * @returns {Promise<boolean>} Whether a dial was placed.
	 */
	async function dialContact(phoneNumber) {
		try {
			const { passed, message, contact: checkedContact } = await checkContact({
				contact: dialingContact,
				phoneNumber,
				profileId: campaignProfileId,
				campaignId: dialingContact.campaignId,
				useCampaignSuppression: true,
			});

			if (!passed) {
				// Hand the decision to the agent instead of blocking the dial.
				const proceed = await requestCheckOverride(message);
				if (!proceed) return false;

				// A failed check still returns the contact with a caller ID assigned,
				// so an override dial keeps the campaign's caller ID. Falls back to
				// dialing the contact as-is if the check returned no contact.
				await placeDial(phoneNumber, checkedContact);
				return true;
			}

			await placeDial(phoneNumber, checkedContact);
			return true;
		} catch (error) {
			console.error('Error dialing callback contact', error);
			enqueueSnackbar('Error dialing contact', { variant: 'error', autoHideDuration: 3000 });
			setCallStatus('');
			setDialActive(false);
			return false;
		}
	}

	/**
	 * Places the dial via the /dial REST endpoint. Assumes the pre-dial check has
	 * either passed or been explicitly overridden by the agent.
	 *
	 * The dialing script comes from the callback configuration (`callbackScriptName`).
	 *
	 * @param {string} phoneNumber The number being dialed.
	 * @param {Object} [checkedContact] The contact returned by the check, whether it
	 *   passed or failed. Dialing this contact carries any assigned outboundCallerId
	 *   through to the dial. When absent the contact is dialed as-is.
	 */
	async function placeDial(phoneNumber, checkedContact) {
		const contact = dialingContactRef.current;
		if (!contact) return;

		const session = await fetchAuthSession();

		const dialBody = {
			contact: checkedContact || { ...contact, phone: phoneNumber },
			field: 'phone',
			dialingProfileId: campaignProfileId,
			skillId,
			agentId,
			campaignId: contact.campaignId,
			segmentId: contact.segmentId,
			...(callbackScriptName ? { scriptName: callbackScriptName } : {}),
		};

		const dialResult = await post({
			apiName: 'cdyxoutreach',
			path: '/dial',
			options: {
				headers: {
					Authorization: `Bearer ${session.tokens.idToken}`,
					'x-api-key': userContext.apiKey,
				},
				body: dialBody,
			},
		}).response;

		const dialResponse = await dialResult.body.json();
		if (!dialResponse.contactId) {
			enqueueSnackbar('Error dialing contact', { variant: 'error', autoHideDuration: 3000 });
			setCallStatus('');
			setDialActive(false);
		}
	}

	/**
	 * Shows the failed-check prompt and waits for the agent's decision.
	 *
	 * @param {string} reason The plain-text reason the check failed.
	 * @returns {Promise<boolean>} True when the agent wants to dial anyway.
	 */
	function requestCheckOverride(reason) {
		// Drop the "Initiating Call..." status while the agent decides.
		setCallStatus('');
		setDialActive(false);
		setPendingCheckFailure({ reason });
		return new Promise((resolve) => {
			checkOverrideResolverRef.current = resolve;
		});
	}

	/**
	 * Resolves the pending failed-check prompt with the agent's decision.
	 *
	 * @param {boolean} proceed True to place the dial anyway, false to cancel it.
	 */
	function resolveCheckOverride(proceed) {
		const resolve = checkOverrideResolverRef.current;
		checkOverrideResolverRef.current = null;
		setPendingCheckFailure(null);

		if (proceed) {
			setDialActive(true);
			setCallStatus('Initiating Call...');
		} else {
			setCallStatus('');
			setDialActive(false);
			// No dial was placed, so the workspace has no active contact. A failed
			// check can only happen on the first dial attempt for a callback (a second
			// call for the same contact is not allowed), so nothing is lost by clearing.
			if (onActiveContactChangeProp) onActiveContactChangeProp(null);
		}

		if (resolve) resolve(proceed);
	}

	/**
	 * Delete callback from the list — sends a retry non-increment disposition (code 729)
	 * so the contact goes back to the pool at the same attempt count.
	 */
	async function handleRemoveCallback() {
		if (!selectedCallback) return;
		setRemoving(true);
		try {
			await sendDisposition({
				callback: selectedCallback,
				contact: contactData,
				dispCode: 729,
				dispClassName: 'Callback Removed - Retry Non Increment',
				dispDescription: 'The callback was removed by the agent. Contact returned to queue without incrementing attempts.',
			});

			enqueueSnackbar('Callback removed', { variant: 'success' });
			setSelectedCallback(null);
			setContactData(null);
			fetchCallbacks();
		} catch (error) {
			console.error('Error removing callback', error);
			enqueueSnackbar('Failed to remove callback', { variant: 'error' });
		} finally {
			setRemoving(false);
		}
	}

	// ─── Reschedule callback ────────────────────────────────────────────────────

	function openRescheduleForm() {
		if (!selectedCallback) return;
		// Default to 30 mins from now
		const thirtyMinsOut = new Date(Date.now() + 30 * 60 * 1000);
		const year = thirtyMinsOut.getFullYear();
		const month = String(thirtyMinsOut.getMonth() + 1).padStart(2, '0');
		const day = String(thirtyMinsOut.getDate()).padStart(2, '0');
		const hours = String(thirtyMinsOut.getHours()).padStart(2, '0');
		const minutes = String(thirtyMinsOut.getMinutes()).padStart(2, '0');
		setRescheduleTime(`${year}-${month}-${day}T${hours}:${minutes}`);
		setRescheduleNotes(selectedCallback.note || '');
		setRescheduleReleasable(selectedCallback.releasable || false);
		setRescheduleOpen(true);
	}

	async function handleRescheduleCallback() {
		if (!selectedCallback || !rescheduleTime) return;
		setRescheduling(true);
		try {
			const session = await fetchAuthSession();
			const response = await put({
				apiName: 'cdyxoutreach',
				path: `/cache/updateCallback/${selectedCallback.campaignId}/${selectedCallback.contactId}`,
				options: {
					headers: {
						Authorization: `Bearer ${session.tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						callDateTime: new Date(rescheduleTime).toISOString(),
						note: rescheduleNotes || null,
						releasable: rescheduleReleasable,
						rescheduledAt: new Date().toISOString(),
					},
				},
			}).response;
			enqueueSnackbar('Callback rescheduled successfully', { variant: 'success' });
			// Un-hide this callback since it was rescheduled (not completed)
			const cbKey = selectedCallback.contactId + ':' + selectedCallback.campaignId;
			setDialedCallbackIds(prev => prev.filter(id => id !== cbKey));
			setRescheduleOpen(false);
			setSelectedCallback(null);
			fetchCallbacks();
		} catch (error) {
			console.error('Error rescheduling callback', error);
			let errMsg = null;
			try {
				if (error?.response?.body) {
					const errBody = await error.response.body.json();
					errMsg = errBody?.msg || errBody?.message || null;
				}
			} catch (e) {
				// Could not parse error body — fall through to default message
			}
			enqueueSnackbar(errMsg || 'Failed to reschedule callback', { variant: 'error' });
		}
		setRescheduling(false);
	}

	// ─── ContactPreview view (when agent clicks "Call Now") ──────────────────────
	if (dialingContact) {
		return (
			<Box display="flex" flexDirection="column" gap={2}>
				<Box display="flex" alignItems="center" gap={1}>
					<Tooltip title={dialActive ? 'Cannot go back while a call is active' : 'Back to callbacks'}>
						<span>
							<IconButton
								size="small"
								disabled={dialActive}
								onClick={handleDialerBack}
							>
								<ArrowBackOutlined fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Typography variant="subtitle2" color="text.secondary">
						Dialing Callback
					</Typography>
				</Box>
				<Fade in={true}>
					<Box>
						<ContactPreview
							contact={dialingContact}
							callStatus={callStatus}
							connectionLost={connectionLost}
							allowManualNumberEntry={false}
							allowMultipleCallsPerContact={false}
							onDismiss={(phoneFieldsAlreadyCalled) => {
								handleDialerBack();
							}}
							onSuppress={handleDialerSuppress}
							onApprove={handleApprove}
							onResetApprove={handleResetApprove}
						/>
					</Box>
				</Fade>

				{/*
					Pre-dial check failure. For callbacks the agent decides whether to
					proceed, so the reason is shown in plain text rather than a toast.
				*/}
				<Dialog
					open={!!pendingCheckFailure}
					onClose={() => resolveCheckOverride(false)}
					maxWidth="sm"
					fullWidth
					aria-labelledby="callback-check-failed-title"
					aria-describedby="callback-check-failed-description"
				>
					<DialogTitle id="callback-check-failed-title">
						<Box display="flex" alignItems="center" gap={1}>
							<WarningAmberOutlined color="warning" />
							Suppression Check Failed
						</Box>
					</DialogTitle>
					<DialogContent>
						<DialogContentText id="callback-check-failed-description" gutterBottom>
							This callback did not pass the pre-dial check for the following reason:
						</DialogContentText>
						<Paper
							variant="outlined"
							sx={{
								p: 1.5,
								borderRadius: '8px',
								bgcolor: alpha(theme.palette.warning.main, 0.08),
								borderColor: alpha(theme.palette.warning.main, 0.4),
							}}
						>
							<Typography variant="body2" color="text.primary" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
								{pendingCheckFailure?.reason || 'No reason was provided.'}
							</Typography>
						</Paper>
						<DialogContentText sx={{ mt: 2 }}>
							Do you want to place the call anyway?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							onClick={() => resolveCheckOverride(false)}
							sx={{ borderRadius: '7px', textTransform: 'none' }}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="warning"
							onClick={() => resolveCheckOverride(true)}
							sx={{ borderRadius: '7px', textTransform: 'none', fontWeight: 600 }}
						>
							Call Anyway
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		);
	}

	// ─── Loading state ──────────────────────────────────────────────────────────
	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" py={6}>
				<CircularProgress />
			</Box>
		);
	}

	// ─── Main UI ────────────────────────────────────────────────────────────────

	// Filter out callbacks that have already been dialed
	const visibleCallbacks = callbacks.filter(
		(cb) => !dialedCallbackIds.includes(cb.contactId + ':' + cb.campaignId)
	);

	// List view groupings
	const now = new Date();
	const overdueCallbacks = visibleCallbacks.filter((cb) => (new Date(cb.callDateTime) - now) / 60000 <= -1440);
	const readyCallbacks = visibleCallbacks.filter((cb) => {
		const diff = (new Date(cb.callDateTime) - now) / 60000;
		return diff > -1440 && diff <= 60;
	});
	const upcomingCallbacks = visibleCallbacks.filter((cb) => (new Date(cb.callDateTime) - now) / 60000 > 60);

	// Calendar view helpers
	const today = new Date();
	const dayOfWeek = today.getDay();
	const monday = new Date(today);
	monday.setDate(today.getDate() - ((dayOfWeek === 0 ? 7 : dayOfWeek) - 1) + (weekOffset * 7));
	monday.setHours(0, 0, 0, 0);

	const weekDays = Array.from({ length: 7 }, (_, i) => {
		const d = new Date(monday);
		d.setDate(monday.getDate() + i);
		return d;
	});

	// Group callbacks by day
	const callbacksByDay = weekDays.map((day) => {
		const dayStart = new Date(day);
		dayStart.setHours(0, 0, 0, 0);
		const dayEnd = new Date(day);
		dayEnd.setHours(23, 59, 59, 999);
		return visibleCallbacks.filter((cb) => {
			const cbDate = new Date(cb.callDateTime);
			return cbDate >= dayStart && cbDate <= dayEnd;
		});
	});

	const isToday = (date) => {
		const t = new Date();
		return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
	};

	// Count for this displayed week
	const weekCount = callbacksByDay.reduce((sum, day) => sum + day.length, 0);

	// Count for today
	const todayStart = new Date();
	todayStart.setHours(0, 0, 0, 0);
	const todayEnd = new Date();
	todayEnd.setHours(23, 59, 59, 999);
	const todayCount = visibleCallbacks.filter((cb) => {
		const d = new Date(cb.callDateTime);
		return d >= todayStart && d <= todayEnd;
	}).length;

	// Week label
	const sunday = new Date(monday);
	sunday.setDate(monday.getDate() + 6);
	const weekLabel = `${monday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${sunday.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

	return (
		<Box display="flex" flexDirection="column" gap={1.5}>
			{/* Top bar: counts + view toggle + refresh */}
			<Box display="flex" alignItems="center" gap={1.5}>
				<Typography variant="caption" color="text.secondary">
					<Box component="span" fontWeight={700} color="text.primary">{todayCount}</Box> today
				</Typography>
				<Typography variant="caption" color="text.secondary">
					<Box component="span" fontWeight={700} color="text.primary">{weekCount}</Box> this week
				</Typography>
				<Box flex={1} />
				<ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small" sx={{ height: 28 }}>
					<ToggleButton value="calendar" sx={{ px: 1, py: 0 }}>
						<CalendarMonthOutlined sx={{ fontSize: 16 }} />
					</ToggleButton>
					<ToggleButton value="list" sx={{ px: 1, py: 0 }}>
						<ViewListOutlined sx={{ fontSize: 16 }} />
					</ToggleButton>
				</ToggleButtonGroup>
				<Tooltip title="Refresh">
					<IconButton onClick={() => { fetchCallbacks(); fetchLastCallForScheduling(); }} disabled={refreshing} size="small">
						{refreshing ? <CircularProgress size={14} /> : <RefreshOutlined sx={{ fontSize: 16 }} />}
					</IconButton>
				</Tooltip>
			</Box>

			{/* ─── Schedule Callback from Last Call ─── */}
			{lastCallContact && !dialingContact && (
				<Card variant="outlined" sx={{ borderRadius: '10px', borderColor: 'divider' }}>
					<CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
						<Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="subtitle2" fontWeight={600}>Last Call Contact</Typography>
							{!scheduleOpen && (
								<Button
									size="small"
									variant="outlined"
									startIcon={<AddAlarmOutlined />}
									onClick={openScheduleForm}
									sx={{ borderRadius: '7px', textTransform: 'none', fontWeight: 600 }}
								>
									Schedule Callback
								</Button>
							)}
						</Box>
						<Divider />
						<Box sx={{ px: 2, py: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
							{lastCallContact.firstName && (
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>Name</Typography>
									<Typography variant="body2">{`${lastCallContact.firstName || ''} ${lastCallContact.lastName || ''}`.trim()}</Typography>
								</Box>
							)}
							{lastCallContact.lastDialedNumber && (
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>Last Dialed</Typography>
									<Typography variant="body2">{lastCallContact.lastDialedNumber}</Typography>
								</Box>
							)}
							{lastCallContact.phone && lastCallContact.phone !== lastCallContact.lastDialedNumber && (
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>Phone</Typography>
									<Typography variant="body2">{lastCallContact.phone}</Typography>
								</Box>
							)}
							{lastCallContact.cell && lastCallContact.cell !== lastCallContact.lastDialedNumber && (
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>Cell</Typography>
									<Typography variant="body2">{lastCallContact.cell}</Typography>
								</Box>
							)}
							{lastCallContact.externalId && (
								<Box>
									<Typography variant="caption" color="text.secondary" fontWeight={600}>External ID</Typography>
									<Typography variant="body2">{lastCallContact.externalId}</Typography>
								</Box>
							)}
						</Box>

						{/* Inline schedule form */}
						<Collapse in={scheduleOpen}>
							<Divider />
							<Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
								<TextField
									label="Callback Date/Time"
									variant="outlined"
									type="datetime-local"
									size="small"
									value={scheduleTime}
									onChange={(e) => setScheduleTime(e.target.value)}
									InputLabelProps={{ shrink: true }}
									helperText="Must be at least 15 minutes from now"
									fullWidth
								/>
								<TextField
									multiline
									rows={2}
									size="small"
									label="Notes"
									variant="outlined"
									value={scheduleNotes}
									onChange={(e) => setScheduleNotes(e.target.value.slice(0, 256))}
									inputProps={{ maxLength: 256 }}
									helperText={`${scheduleNotes.length}/256`}
									fullWidth
								/>
								{/* <FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={scheduleReleasable}
											onChange={(e) => setScheduleReleasable(e.target.checked)}
										/>
									}
									label={
										<Typography variant="caption" color="text.secondary">
											Release contact back to the campaign if not dialled by end of the scheduled day
										</Typography>
									}
								/> */}
								<Box display="flex" gap={1}>
									<Button
										size="small"
										variant="contained"
										onClick={handleScheduleCallback}
										disabled={scheduling || !scheduleTime}
										sx={{ borderRadius: '7px', textTransform: 'none', fontWeight: 600 }}
									>
										{scheduling ? <CircularProgress size={16} /> : 'Confirm'}
									</Button>
									<Button
										size="small"
										onClick={() => setScheduleOpen(false)}
										disabled={scheduling}
										sx={{ borderRadius: '7px', textTransform: 'none' }}
									>
										Cancel
									</Button>
								</Box>
							</Box>
						</Collapse>
					</CardContent>
				</Card>
			)}

			{/* ─── Calendar View ─── */}
			{viewMode === 'calendar' && (
				<>
					{/* Week navigation */}
					<Box display="flex" alignItems="center" justifyContent="space-between">
						<IconButton size="small" onClick={() => setWeekOffset(prev => prev - 1)}>
							<ChevronLeftOutlined fontSize="small" />
						</IconButton>
						<Typography variant="caption" fontWeight={600} color="text.primary">
							{weekLabel}
						</Typography>
						<IconButton size="small" onClick={() => setWeekOffset(prev => prev + 1)}>
							<ChevronRightOutlined fontSize="small" />
						</IconButton>
					</Box>

					{/* Weekly calendar grid */}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(7, 1fr)',
							gap: 0.5,
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: '10px',
							overflow: 'hidden',
						}}
					>
						{/* Day headers */}
						{weekDays.map((day, i) => (
							<Box
								key={`header-${i}`}
								sx={{
									py: 0.75,
									textAlign: 'center',
									bgcolor: isToday(day) ? 'primary.main' : 'action.hover',
									borderBottom: '1px solid',
									borderColor: 'divider',
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color={isToday(day) ? 'primary.contrastText' : 'text.secondary'}
									sx={{ fontSize: '0.65rem', textTransform: 'uppercase' }}
								>
									{day.toLocaleDateString(undefined, { weekday: 'short' })}
								</Typography>
								<Typography
									variant="caption"
									display="block"
									fontWeight={600}
									color={isToday(day) ? 'primary.contrastText' : 'text.primary'}
									sx={{ fontSize: '0.7rem' }}
								>
									{day.getDate()}
								</Typography>
							</Box>
						))}

						{/* Day columns with callback slots */}
						{weekDays.map((day, i) => (
							<Box
								key={`col-${i}`}
								sx={{
									minHeight: 80,
									p: 0.5,
									display: 'flex',
									flexDirection: 'column',
									gap: 0.5,
									bgcolor: isToday(day) ? alpha(theme.palette.primary.main, 0.03) : 'transparent',
								}}
							>
								{callbacksByDay[i].map((callback, idx) => {
									const status = getStatusInfo(callback);
									const isSelected = selectedCallback?.contactId === callback.contactId;

									return (
										<Box
											key={`${callback.contactId}-${idx}`}
											onClick={(e) => { e.stopPropagation(); setSelectedCallback(isSelected ? null : callback); }}
											sx={{
												px: 0.75,
												py: 0.5,
												borderRadius: '6px',
												cursor: 'pointer',
												border: '1px solid',
												borderColor: isSelected ? 'primary.main' : 'transparent',
												bgcolor: status.label === 'Overdue'
													? alpha(theme.palette.error.main, 0.08)
													: status.label === 'Due Now'
														? alpha(theme.palette.success.main, 0.08)
														: alpha(theme.palette.info.main, 0.06),
												transition: 'all 0.15s ease',
												'&:hover': {
													bgcolor: alpha(theme.palette.primary.main, 0.1),
													borderColor: 'primary.light',
												},
											}}
										>
											<Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem', lineHeight: 1.3 }}>
												{new Date(callback.callDateTime).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
											</Typography>
											<Typography variant="caption" display="block" color="text.secondary" noWrap sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}>
												{callback.callBackPOC || 'No #'}
											</Typography>
										</Box>
									);
								})}
							</Box>
						))}
					</Box>
				</>
			)}

			{/* ─── List View ─── */}
			{viewMode === 'list' && (
				<Box display="flex" flexDirection="column" gap={1}>
					<CallbackSection
						title="Overdue"
						count={overdueCallbacks.length}
						color="error.main"
						expanded={expandedSections.overdue}
						onToggle={() => toggleSection('overdue')}
						callbacks={overdueCallbacks}
						selectedCallback={selectedCallback}
						onSelect={(cb) => setSelectedCallback(selectedCallback?.contactId === cb.contactId ? null : cb)}
						theme={theme}
					/>
					<CallbackSection
						title="Ready"
						count={readyCallbacks.length}
						color="success.main"
						expanded={expandedSections.ready}
						onToggle={() => toggleSection('ready')}
						callbacks={readyCallbacks}
						selectedCallback={selectedCallback}
						onSelect={(cb) => setSelectedCallback(selectedCallback?.contactId === cb.contactId ? null : cb)}
						theme={theme}
					/>
					<CallbackSection
						title="Upcoming"
						count={upcomingCallbacks.length}
						color="info.main"
						expanded={expandedSections.upcoming}
						onToggle={() => toggleSection('upcoming')}
						callbacks={upcomingCallbacks}
						selectedCallback={selectedCallback}
						onSelect={(cb) => setSelectedCallback(selectedCallback?.contactId === cb.contactId ? null : cb)}
						theme={theme}
					/>
				</Box>
			)}

			{/* ─── Selected callback detail panel (shared) ─── */}
			{selectedCallback && (
				<Card variant="outlined" sx={{ borderRadius: '10px', borderColor: 'primary.main' }}>
					<CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
						<Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="subtitle2" fontWeight={600}>
								{new Date(selectedCallback.callDateTime).toLocaleString(undefined, {
									weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
								})}
							</Typography>
							<Chip
								label={getStatusInfo(selectedCallback).label}
								color={getStatusInfo(selectedCallback).color}
								variant={getStatusInfo(selectedCallback).variant}
								size="small"
								sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
							/>
						</Box>
						<Divider />
						<Box sx={{ px: 2, py: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
							<FieldItem label="Phone Number" value={selectedCallback.callBackPOC || '—'} color="primary.main" />
							{selectedCallback.createdByAgentName && (
								<FieldItem label="Created By" value={selectedCallback.createdByAgentName} color="primary.main" />
							)}
							{selectedCallback.note && (
								<FieldItem label="Note" value={selectedCallback.note} color="primary.main" />
							)}
						</Box>
						{contactData && (
							<>
								<Divider />
								<Box sx={{ px: 2, py: 1.25, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
									<FieldItem label="Contact Name" value={`${contactData.firstName || ''} ${contactData.lastName || ''}`.trim() || '—'} color="primary.main" />
									{contactData.externalId && <FieldItem label="External ID" value={contactData.externalId} color="primary.main" />}
									{contactData.email && <FieldItem label="Email" value={contactData.email} color="primary.main" />}
									{(contactData.phone && contactData.phone !== selectedCallback.callBackPOC) && <FieldItem label="Primary Phone" value={contactData.phone} color="primary.main" />}
								</Box>
							</>
						)}
						{!contactData && (
							<Box display="flex" justifyContent="center" py={2}>
								<CircularProgress size={18} />
							</Box>
						)}
						{contactData && (
							<>
								<Divider />
								<Box sx={{ px: 2, py: 1.25, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
									<Button variant="contained" size="small" startIcon={<CallOutlined />} onClick={handleDial} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
										Call Now
									</Button>
									<Button variant="outlined" size="small" startIcon={<EditCalendarOutlined />} onClick={openRescheduleForm} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
										Reschedule
									</Button>
									<Button variant="outlined" size="small" color="error" startIcon={removing ? <CircularProgress size={14} color="error" /> : <DeleteOutline />} disabled={removing} onClick={handleRemoveCallback} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
										{removing ? 'Removing...' : 'Delete'}
									</Button>
								</Box>
								<Collapse in={rescheduleOpen}>
									<Divider />
									<Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
										<TextField
											label="New Callback Date/Time"
											variant="outlined"
											type="datetime-local"
											size="small"
											value={rescheduleTime}
											onChange={(e) => setRescheduleTime(e.target.value)}
											InputLabelProps={{ shrink: true }}
											helperText="Must be at least 15 minutes from now"
											fullWidth
										/>
										<TextField
											multiline
											rows={2}
											size="small"
											label="Notes"
											variant="outlined"
											value={rescheduleNotes}
											onChange={(e) => setRescheduleNotes(e.target.value.slice(0, 256))}
											inputProps={{ maxLength: 256 }}
											helperText={`${rescheduleNotes.length}/256`}
											fullWidth
										/>
										{/* <FormControlLabel
											control={
												<Checkbox
													size="small"
													checked={rescheduleReleasable}
													onChange={(e) => setRescheduleReleasable(e.target.checked)}
												/>
											}
											label={
												<Typography variant="caption" color="text.secondary">
													Release contact back to the campaign if not dialled by end of the scheduled day
												</Typography>
											}
										/> */}
										<Box display="flex" gap={1}>
											<Button
												size="small"
												variant="contained"
												onClick={handleRescheduleCallback}
												disabled={rescheduling || !rescheduleTime}
												sx={{ borderRadius: '7px', textTransform: 'none', fontWeight: 600 }}
											>
												{rescheduling ? <CircularProgress size={16} /> : 'Confirm Reschedule'}
											</Button>
											<Button
												size="small"
												onClick={() => setRescheduleOpen(false)}
												disabled={rescheduling}
												sx={{ borderRadius: '7px', textTransform: 'none' }}
											>
												Cancel
											</Button>
										</Box>
									</Box>
								</Collapse>
							</>
						)}
					</CardContent>
				</Card>
			)}

			{/* Empty state */}
			{visibleCallbacks.length === 0 && (
				<Paper variant="outlined" sx={{ py: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, borderStyle: 'dashed', borderRadius: '12px' }}>
					<AccessTimeOutlined sx={{ fontSize: 36, color: 'text.disabled' }} />
					<Typography variant="body2" color="text.secondary">No callbacks scheduled</Typography>
				</Paper>
			)}
		</Box>
	);
}

// ─── List view collapsible section ──────────────────────────────────────────

function CallbackSection({ title, count, color, expanded, onToggle, callbacks, selectedCallback, onSelect, theme }) {
	return (
		<Box>
			<Box
				onClick={onToggle}
				sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', py: 0.75, px: 1, borderRadius: '8px', '&:hover': { bgcolor: 'action.hover' } }}
			>
				{expanded ? <ExpandLess sx={{ fontSize: 18, color }} /> : <ExpandMore sx={{ fontSize: 18, color }} />}
				<Typography variant="subtitle2" fontWeight={600} color={color}>{title}</Typography>
				<Chip label={count} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: alpha(theme.palette.text.primary, 0.08) }} />
			</Box>
			<Collapse in={expanded}>
				<Box sx={{ pl: 1, pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
					{callbacks.length === 0 && (
						<Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>None</Typography>
					)}
					{callbacks.map((callback, idx) => {
						const isSelected = selectedCallback?.contactId === callback.contactId;
						return (
							<Box
								key={`${callback.contactId}-${idx}`}
								onClick={() => onSelect(callback)}
								sx={{
									px: 1.5, py: 0.75, borderRadius: '8px', cursor: 'pointer',
									border: '1px solid', borderColor: isSelected ? 'primary.main' : 'divider',
									bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
									transition: 'all 0.15s ease',
									'&:hover': { borderColor: 'primary.light', bgcolor: alpha(theme.palette.primary.main, 0.04) },
								}}
							>
								<Box display="flex" alignItems="center" justifyContent="space-between">
									<Typography variant="caption" fontWeight={600}>
										{new Date(callback.callDateTime).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
									</Typography>
								</Box>
								<Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
									{callback.callBackPOC || 'No number'}
								</Typography>
							</Box>
						);
					})}
				</Box>
			</Collapse>
		</Box>
	);
}

/** Field item — colored label above the value */
function FieldItem({ label, value, color = 'primary.main' }) {
	return (
		<Box display="flex" flexDirection="column" gap={0.25}>
			<Typography variant="caption" color={color} fontWeight={600}>{label}</Typography>
			<Typography variant="body2" fontWeight={500} color="text.primary">{value}</Typography>
		</Box>
	);
}
